import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import useRequest from "../../hooks/useRequest";
import { createSlug } from "../../util/fn";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import {
  Input,
  CKEditorInput,
  SelectInput,
  RenderInputFields,
  SubmitButton,
  SubTab,
  SubInput,
  Textarea,
} from "../Form/Form";

const Add = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    trigger,
    control,
    getValues,
    clearErrors,
  } = useForm();

  const history = useHistory();
  const enTextRef = useRef();
  const arTextRef = useRef();

  const { languages } = useSelector((state) => state.setting);

  const [emailActions, setEmailActions] = useState([]);
  const [ActionData, setActionData] = useState({});
  const [emailConstants, setEmailConstants] = useState([]);

  const { response, request } = useRequest();
  const { response: responseEmailActions, request: requestEmailActions } =
    useRequest();
  const { response: responseConstants, request: requestConstants } =
    useRequest();
  const { response: responseActionData, request: requestActionData } =
    useRequest();

  useEffect(() => {
    // register("body", { required: true });
    requestEmailActions("GET", "notification-action/all");
    document.title = "Add Notification Template - Easyspare";
  }, []);

  useEffect(() => {
    if (responseEmailActions) {
      setEmailActions(responseEmailActions?.notificationActions || []);
    }
  }, [responseEmailActions]);

  useEffect(() => {
    if (responseConstants) {
      setEmailConstants(responseConstants?.notificationAction?.constants);
    }
  }, [responseConstants]);

  useEffect(() => {
    if (responseActionData) {
      setActionData(responseActionData);
    }
  }, [responseActionData]);

  useEffect(() => {
    if (response) {
      toast.success("Notification Template has been added successfully.");
      history.push("/notification-template");
    }
  }, [response]);

  const [selectedActionData, setSelectedAction] = useState("");

  const constantHandler = (id) => {
    if (!id) {
      return;
    }
    // Find the selected action in emailActions by its ID
    const selectedActionObject = emailActions.find(
      (action) => action._id === id
    );
    if (selectedActionObject) {
      // Update the selectedAction state variable with the value of action.action
      setSelectedAction(selectedActionObject.action);
    }
    requestConstants("GET", `notification-action/${id}`);
  };

  const [activeTab, setActiveTab] = useState("en"); // State to track active tab index

  const handleTabChange = (lang) => {
    setActiveTab(lang); // Update active tab index when tab is changed
  };

  const insertConstantHandler = () => {
    const constant = getValues("constant");
    // console.log(constant);
    if (constant.length === 0) {
      return;
    }
    // Determine the key based on the active tab
    const key = activeTab;
    // Get the current value of the body textarea
    let bodyObject = getValues("body-" + key);

    // Concatenate the constant with the current value
    bodyObject = bodyObject + "$" + `{${constant}}`;

    // Set the new value back into the body textarea
    setValue("body-" + key, bodyObject);
  };

  // // Function to extract dynamic variables from body content
  const extractVariables = (bodyContent) => {
    const regex = /\{([^\}]+)\}/g;
    const matches = bodyContent.match(regex);
    if (matches) {
      return matches.map((match) => match.replace(/\{|\}/g, ""));
    }
    return [];
  };

  // Function to generate body content with dynamic variables
  const generateBody = (variables) => {
    let body = "";
    variables.forEach((variable) => {
      body += `{${variable}} `;
    });
    return body.trim();
  };

  const onSubmit = (data) => {
    if (!data || Object.keys(data).length === 0) {
      // Data is null or empty
      // Display an error message to the user, e.g., using toast
      toast.error("Form data is empty! Please fill in the required fields.");
      return;
    }
    // console.log("data>>>>>>>", data);

    // Extract relevant information from the form data
    const { name, action, category } = data;

    // // Extract action using the name instead of value

    let translations = {};
    languages?.forEach((lang) => {
      translations[lang.code] = {
        subject: data[`subject-${lang.code}`],
        body: data[`body-${lang.code}`],
        variables: extractVariables(data[`body-${lang.code}`]),
      };
    });

    // Generate messages
    const Message = {
      name,
      action: selectedActionData,
      category,
      isActive: true,
    };
    Message.translations = translations;
    // console.log("Message:");

    if (Message) {
      request("POST", "notification-template", Message);
    }

    // Proceed with any further logic
    // console.log("Form data received:", data);
  };

  // Function to insert constants into body content
  const insertConstants = (bodyContent, constant) => {
    return bodyContent.replace(/{constant}/g, constant);
  };

  const InputFields = [
    [
      {
        Component: Input,
        label: "Name",
        type: "text",
        name: "name",
        registerFields: {
          required: true,
        },
      },
      {
        Component: SelectInput,
        label: "Category",
        name: "category",
        registerFields: {
          required: true,
        },
        children: (
          <>
            <option value="">{"Select Category"}</option>
            <option value="order">Order </option>
            <option value="orderOffer">Order Offer </option>
            <option value="orderCancel">Order Cancel </option>
            <option value="orderReturn">Order Return </option>
            <option value="orderConfirm">Order Confirm </option>
            <option value="orderComplete">Order Complete </option>
            <option value="paymentHistory">Payment History </option>
            <option value="userProfile">User Profile </option>
            <option value="userProfile">Brand Request</option>
          </>
        ),
      },
      {
        Component: SelectInput,
        label: "Action",
        name: "action",
        registerFields: {
          required: true,
          minLength: 1,
        },
        onChange: constantHandler,
        children: (
          <>
            <option value="">{"Select Page Name"}</option>
            {emailActions.length > 0 &&
              emailActions.map((action) => (
                <option
                  key={action._id}
                  value={action._id}
                  name={action.action}
                >
                  {action.action}
                </option>
              ))}
          </>
        ),
      },
      {
        Component: SelectInput,
        label: "Constants",
        name: "constant",
        registerFields: {},
        children: (
          <>
            <option value="">{"Select Constant"}</option>
            {emailConstants?.length > 0 &&
              emailConstants?.map((constant, index) => (
                <option key={index} value={constant}>
                  {constant}
                </option>
              ))}
          </>
        ),
        moreData: (
          <button
            onClick={insertConstantHandler}
            type="button"
            className="btn btn-success font-weight-bold text-uppercase px-9 py-4"
          >
            Insert
          </button>
        ),
      },
    ],
  ];

  return (
    <div
      className="content  d-flex flex-column flex-column-fluid"
      id="kt_content"
    >
      <Breadcrumb
        title="Add Notification Template"
        links={[
          { to: "/", name: "Dashboard" },
          {
            to: "/notification-template",
            name: "Back To Notification Template",
          },
        ]}
      />

      <div className="d-flex flex-column-fluid">
        <div className=" container ">
          <div className="card card-custom">
            <div className="card-header">
              <h3 className="card-title">Add New Notification Template</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-xl-12">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="card card-custom gutter-b">
                      <RenderInputFields
                        InputFields={InputFields}
                        errors={errors}
                        register={register}
                      />
                      <div className="card-header card-header-tabs-line">
                        <div className="card-toolbar">
                          <ul
                            className="nav nav-tabs nav-tabs-space-lg nav-tabs-line nav-bold nav-tabs-line-3x"
                            role="tablist"
                          >
                            {languages.length > 0 &&
                              languages.map((lang, index) => (
                                <SubTab
                                  key={lang.code}
                                  name={lang.name}
                                  index={index}
                                  image={lang?.image}
                                  tabName={`language_${index}`}
                                  onClick={() => handleTabChange(lang.code)}
                                />
                              ))}
                          </ul>
                        </div>
                      </div>
                      <div className="card-body px-0">
                        <div className="tab-content px-10">
                          {languages.length > 0 &&
                            languages.map((lang, index) => (
                              <SubInput
                                key={index}
                                index={index}
                                errors={errors}
                                register={register}
                                required={true}
                                control={control}
                                code={lang.code}
                                tabName={`language_${index}`}
                                InputFields={[
                                  // Language-specific input fields for each language
                                  // For example, for English (lang.code === 'en')
                                  [
                                    {
                                      Component: Input,
                                      label: "Subject",
                                      type: "text",
                                      name: `subject`,
                                      registerFields: {
                                        required: true,
                                      },
                                    },

                                    {
                                      Component: Textarea,
                                      colClass: "col-xl-12 h-100",
                                      textareaHeight: "height-full",
                                      label: "Body",
                                      name: `body`,
                                      registerFields: {
                                        required: true,
                                        setValueAs: (v) => v.trim(),
                                      },
                                    },
                                  ],
                                ]}
                              />
                            ))}
                        </div>
                      </div>
                    </div>

                    <SubmitButton
                      handleSubmit={handleSubmit}
                      onSubmit={onSubmit}
                      name="Submit"
                      pxclassName="px-10"
                    />
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Add;
